import { getDefaultStore } from 'jotai';
import { selectedFacilityAtom, type Facility } from '@/store/facilityAtom';
import { siteGroupExperienceAtom } from '@/store/siteGroupExperienceAtom';
import { readPersistedFacility } from '@/features/scheduling/lib/selfRegPriorContext';

/**
 * Parking Lot Mobile Mammo (`/mobile/{guid}`) — D10 PreviousSiteGroup.
 * When a logged-in patient opens a flyer for SiteGroup B while on SiteGroup A,
 * branding switches to B for the clinic-day session. On cancel/finish, restore A.
 *
 * Distinct from Self-Reg interrupt stash (`pp_self_reg_prior_context` / D13).
 */
export const MOBILE_CLINIC_DAY_PRIOR_CONTEXT_KEY = 'pp_mobile_clinic_day_prior_context';

/**
 * Flyer GUID from `/mobile/{guid}` entry. Survives navigation to /schedule or /guest-schedule
 * so "Start Over" can return to that deep link. Cleared on D10 restore / exit.
 */
export const MOBILE_CLINIC_DAY_ENTRY_GUID_KEY = 'pp_mobile_clinic_day_entry_guid';

export interface MobileClinicDayPriorContext {
  facility: Facility | null;
  returnPath: string;
}

export function setMobileClinicDayEntryGuid(guid: string): void {
  const g = guid.trim();
  if (!/^[0-9a-fA-F-]{36}$/.test(g)) return;
  sessionStorage.setItem(MOBILE_CLINIC_DAY_ENTRY_GUID_KEY, g);
}

export function loadMobileClinicDayEntryGuid(): string | null {
  const g = sessionStorage.getItem(MOBILE_CLINIC_DAY_ENTRY_GUID_KEY)?.trim();
  return g && /^[0-9a-fA-F-]{36}$/.test(g) ? g : null;
}

export function clearMobileClinicDayEntryGuid(): void {
  sessionStorage.removeItem(MOBILE_CLINIC_DAY_ENTRY_GUID_KEY);
}

/** Public Parking Lot Mobile entry path for a flyer GUID. */
export function buildMobileClinicDayEntryPath(guid: string): string {
  return `/mobile/${encodeURIComponent(guid.trim())}`;
}

export function loadMobileClinicDayPriorContext(): MobileClinicDayPriorContext | null {
  try {
    const raw = sessionStorage.getItem(MOBILE_CLINIC_DAY_PRIOR_CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MobileClinicDayPriorContext;
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      facility: parsed.facility ?? null,
      returnPath:
        typeof parsed.returnPath === 'string' && parsed.returnPath.startsWith('/')
          ? parsed.returnPath
          : '/appointments',
    };
  } catch {
    return null;
  }
}

export function clearMobileClinicDayPriorContext(): void {
  sessionStorage.removeItem(MOBILE_CLINIC_DAY_PRIOR_CONTEXT_KEY);
}

/**
 * Capture the patient's facility before applying the flyer SiteGroup.
 * Never stash the flyer SiteGroup itself as "prior".
 */
export function captureMobileClinicDayPriorContext(partial: {
  facility?: Facility | null;
  returnPath?: string;
  /** Flyer SiteGroup being entered — excluded from prior stash. */
  linkSiteGroup?: string;
}): void {
  const existing = loadMobileClinicDayPriorContext();
  const link = partial.linkSiteGroup?.trim();

  const usable = (f: Facility | null | undefined): Facility | null => {
    if (!f?.id) return null;
    if (link && f.id.toLowerCase() === link.toLowerCase()) return null;
    return f;
  };

  const facility =
    usable(existing?.facility)
    ?? usable(partial.facility)
    ?? usable(readPersistedFacility());

  if (!facility) return;

  const next: MobileClinicDayPriorContext = {
    facility,
    returnPath:
      existing?.returnPath
      ?? (partial.returnPath?.startsWith('/') ? partial.returnPath : '/appointments'),
  };
  sessionStorage.setItem(MOBILE_CLINIC_DAY_PRIOR_CONTEXT_KEY, JSON.stringify(next));
}

export type MobileClinicDayRestoreAppliers = {
  setFacility: (f: Facility | null) => void;
};

/** Restore prior SiteGroup branding after clinic-day exit. Returns navigate path. */
export function restoreMobileClinicDayPriorContext(
  appliers?: MobileClinicDayRestoreAppliers,
): string {
  const ctx = loadMobileClinicDayPriorContext();
  clearMobileClinicDayPriorContext();
  clearMobileClinicDayEntryGuid();
  sessionStorage.removeItem('pp_facility_from_url_param');

  if (!ctx) return '/appointments';

  if (ctx.facility) {
    if (appliers?.setFacility) appliers.setFacility(ctx.facility);
    else getDefaultStore().set(selectedFacilityAtom, ctx.facility);
    try {
      localStorage.setItem('selected_facility', JSON.stringify(ctx.facility));
    } catch { /* ignore quota */ }
    // Drop flyer experience so useSiteGroupExperience reloads the restored SiteGroup's theme.
    try {
      localStorage.removeItem('site_group_experience');
    } catch { /* ignore */ }
    getDefaultStore().set(siteGroupExperienceAtom, null);
  }

  return ctx.returnPath || '/appointments';
}
